import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Sidebar, Container, Responsive, Icon, Label } from 'semantic-ui-react';
import i18next from 'i18next';
import api, { RELOAD } from '../../api/paidit-api';
import Categories from './menu/Categories';
import MenuItemsGroup from './menu/MenuItemsGroup';
import Cart from './cart/Cart';
import UpsellsModal from './menu/UpsellsModal';
import BranchCard from '../branches/BranchCard';
import ItemToConstruction from './cart/ItemToConstruction';
import Loading from '../loading/Loading';
import { PIRedirect } from '../nav';
import { startOrder, setSelectedCategory, setCartModalVisibility } from '../../actions';
import { getMenu, getCart } from '../../selectors';
import { useEventListener, usePageTitle } from '../../hooks';
import checkoutService from '../../services/checkout';
import OrderPrompt from './OrderPrompt';

import './Order.scss';

const Order = () => {
    const dispatch = useDispatch();
    const isLoading = useSelector(state => state.loading.queue.includes('orders/create'));
    const branch = useSelector(state => state.business.branch);
    const tableNumber = useSelector(state => state.order.tableNumber);
    const showCartModal = useSelector(state => state.ui.showCartModal);
    const cart = useSelector(getCart);
    const menu = useSelector(getMenu);
    const [narrowMode, setNarrowMode] = useState(window.innerWidth <= 768);
    const isScrollingTimeoutHandler = useRef();
    const preventCategorySelectionOnScrollHandler = useRef(false);

    const categoriesRefs = menu.hierarchy.reduce((acc, category) => {
        acc[category.id] = React.createRef();
        return acc;
    }, {});

    const handleResponsivesUpdate = (e, { width }) => setNarrowMode(width <= 768);

    const handleScroll = useCallback(event => {
        clearTimeout(isScrollingTimeoutHandler.current);

        isScrollingTimeoutHandler.current = setTimeout(function () {
            preventCategorySelectionOnScrollHandler.current = false;
        }, 66);

        if (preventCategorySelectionOnScrollHandler.current) return;

        if (event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight) {
            const last = menu.hierarchy[menu.hierarchy.length - 1];
            if (last) return dispatch(setSelectedCategory(last.id));
        }

        const additionalOffsetTop = 100 + (narrowMode ? 40 : 0);
        const selectedId = Object.keys(categoriesRefs).filter(key => categoriesRefs[key].current).sort((a, b) => categoriesRefs[a].current.offsetTop - categoriesRefs[b].current.offsetTop).find(key => {
            return categoriesRefs[key].current.offsetTop + categoriesRefs[key].current.offsetHeight - event.target.scrollTop - additionalOffsetTop >= 0;
        });
        if (selectedId) {
            dispatch(setSelectedCategory(selectedId));
        }
    }, [categoriesRefs, dispatch, menu.hierarchy, narrowMode]);

    useEventListener('scroll', handleScroll, () => document.getElementsByClassName('pi-items-container')[0]);

    useEffect(() => {
        return () => {
            dispatch(setCartModalVisibility(false));
        };
    }, [dispatch]);

    useEffect(() => {
        if (cart.cartId) checkoutService.validateCheckout();
    }, [cart.cartId]);

    useEffect(() => {
        if (branch && !cart.cartId) {
            api({ rejectOnError: false, alertError: { type: RELOAD } }).order.start(branch.id).then(order => dispatch(startOrder(order)));
        }
    }, [branch, cart.cartId, dispatch]);

    usePageTitle('');

    if (!branch || !branch.id) {
        return <PIRedirect to='/' />;
    }

    const handleCategoryClick = id => {
        if (narrowMode) {
            preventCategorySelectionOnScrollHandler.current = true;
            dispatch(setSelectedCategory(id));
        }

        setTimeout(() => {
            categoriesRefs[id].current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
    };

    return (
        <div className="pi-order-page">
            <Loading showLoading={isLoading} />
            <OrderPrompt excludePaths={["/checkout"]} />
            <div className="pi-categories-container">
                <Categories onCategorySelect={handleCategoryClick} horizontal={narrowMode} />
            </div>
            <div
                className="pi-items-container"
                style={{ overflow: showCartModal ? 'hidden' : 'auto', marginTop: narrowMode ? 35 : 0 }}
            >
                {menu.hierarchy.map(cat => (
                    <div key={cat.id} ref={categoriesRefs[cat.id]}>
                        <MenuItemsGroup categoryId={cat.id} />
                    </div>
                ))}
                <div className="mobile-margin" />
                <Responsive
                    maxWidth={768}
                    className="mobile-cart-header"
                    onUpdate={handleResponsivesUpdate}
                >
                    <div className="cart-btn-container" onClick={() => dispatch(setCartModalVisibility(true))}>
                        {tableNumber ?
                            <Label basic><Icon name='food' />{tableNumber}</Label>
                            :
                            <Icon name="shopping cart" size="big" />
                        }
                        {i18next.t('order.myCart')}
                        <span>({cart.items.length})</span>
                    </div>
                </Responsive>
            </div>
            <Sidebar
                as={Container}
                animation="scale down"
                direction="bottom"
                vertical="true"
                visible={showCartModal}
                width='very wide'
            >
                <div className="pi-modal-cart-container">
                    {showCartModal && <Cart closeCart={() => dispatch(setCartModalVisibility(false))} />}
                </div>
            </Sidebar>
            <div className="pi-cart-container">
                <BranchCard />
                <Cart readOnly={false} />
            </div>
            <ItemToConstruction />
            <UpsellsModal />
        </div>
    );
};

export default Order;
